import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { createPDF, brandedAutoTable, addFooter, addHeader } from "@/utils/pdfUtils";
import { 
  Zap, ArrowLeft, Calendar, BarChart3, FileSpreadsheet, 
  FileText, TrendingUp, LogOut, GitCompare
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReports() {
  const navigate = useNavigate();
  const [yearlyData, setYearlyData] = useState({ years: [], summary: null });
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const yearlyRes = await axios.get(`${API}/reports/yearly`, getAuthHeaders());
      setYearlyData(yearlyRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const exportYearlyToExcel = () => {
    if (yearlyData.years.length === 0) {
      toast.error("No data to export");
      return;
    }

    const data = yearlyData.years.map((y) => ({
      Year: y.year,
      Events: y.event_count,
      Responses: y.response_count,
      "Avg TeamPower": y.avg_teampower_index,
      "Avg Capability": y.avg_capability_score,
      "Avg Collaboration": y.avg_collaboration_score,
      "Avg Energy": y.avg_energy_score,
    }));

    if (yearlyData.summary) {
      data.push({});
      data.push({
        Year: "TOTAL",
        Events: yearlyData.summary.total_events,
        Responses: yearlyData.summary.total_responses,
        "Avg TeamPower": yearlyData.summary.avg_teampower_index,
        "Avg Capability": yearlyData.summary.avg_capability_score,
        "Avg Collaboration": yearlyData.summary.avg_collaboration_score,
        "Avg Energy": yearlyData.summary.avg_energy_score,
      });
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Yearly Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `TeamPower_Yearly_Report.xlsx`);
    toast.success("Excel exported!");
  };

  const exportYearlyToPDF = () => {
    if (yearlyData.years.length === 0) {
      toast.error("No data to export");
      return;
    }

    const doc = createPDF();

    const contentY = addHeader(doc);

    doc.setFontSize(16);
    doc.setTextColor(236, 72, 153);
    doc.text("Yearly Report", 105, contentY + 2, { align: "center" });

    if (yearlyData.summary) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text("Overall Summary", 14, contentY + 18);
      doc.setFontSize(12);
      doc.text(`Total Events: ${yearlyData.summary.total_events}`, 14, contentY + 28);
      doc.text(`Total Responses: ${yearlyData.summary.total_responses}`, 14, contentY + 35);
      doc.text(`Average TeamPower Index: ${yearlyData.summary.avg_teampower_index}`, 14, contentY + 42);
    }

    const tableData = yearlyData.years.map((y) => [
      y.year,
      y.event_count,
      y.response_count,
      y.avg_teampower_index,
      y.avg_capability_score,
      y.avg_collaboration_score,
      y.avg_energy_score,
    ]);

    brandedAutoTable(doc, {
      startY: contentY + 52,
      head: [["Year", "Events", "Responses", "TeamPower", "Capability", "Collaboration", "Energy"]],
      body: tableData,
      theme: "grid",
    });

    addFooter(doc);
    doc.save("TeamPower_Yearly_Report.pdf");
    toast.success("PDF exported!");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Prepare chart data
  const chartData = yearlyData.years.map((y) => ({
    year: y.year,
    TeamPower: y.avg_teampower_index,
    Capability: y.avg_capability_score,
    Collaboration: y.avg_collaboration_score,
    Energy: y.avg_energy_score,
  }));

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-zinc-900/50 border-r border-zinc-800 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <p className="font-heading text-sm tracking-wide">TEAMPOWER</p>
            <p className="text-xs text-zinc-500">Admin</p>
          </div>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="sidebar-link w-full"
          >
            <Calendar className="w-5 h-5" />
            Events
          </button>
          <button className="sidebar-link active w-full">
            <BarChart3 className="w-5 h-5" />
            Reports
          </button>
          <button
            onClick={() => navigate("/admin/comparison")}
            className="sidebar-link w-full"
          >
            <GitCompare className="w-5 h-5" />
            Team Comparison
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-zinc-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span className="font-heading text-sm">REPORTS</span>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")} className="text-zinc-400 hover:text-white" title="Events">
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/comparison")} className="text-zinc-400 hover:text-white" title="Team Comparison">
              <GitCompare className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" title="Logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <main className="p-6 md:p-8">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl tracking-wide">REPORTS</h1>
              <p className="text-zinc-500">Yearly overview and analysis</p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={exportYearlyToExcel}
                className="bg-zinc-800 hover:bg-zinc-700 text-white"
                data-testid="export-excel-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button
                variant="secondary"
                onClick={exportYearlyToPDF}
                className="bg-zinc-800 hover:bg-zinc-700 text-white"
                data-testid="export-pdf-button"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>

          {/* Overall Summary */}
          {yearlyData.summary && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8"
            >
              <div className="bg-zinc-900/50 border border-orange-500/30 rounded-xl p-4 col-span-2">
                <p className="text-zinc-500 text-sm mb-1">Overall TeamPower</p>
                <p className="text-4xl font-bold text-orange-400">{yearlyData.summary.avg_teampower_index}</p>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <p className="text-zinc-500 text-sm mb-1">Total Events</p>
                <p className="text-2xl font-bold text-white">{yearlyData.summary.total_events}</p>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <p className="text-zinc-500 text-sm mb-1">Total Responses</p>
                <p className="text-2xl font-bold text-white">{yearlyData.summary.total_responses}</p>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <p className="text-zinc-500 text-sm mb-1">Capability</p>
                <p className="text-2xl font-bold text-blue-400">{yearlyData.summary.avg_capability_score}</p>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <p className="text-zinc-500 text-sm mb-1">Collaboration</p>
                <p className="text-2xl font-bold text-purple-400">{yearlyData.summary.avg_collaboration_score}</p>
              </div>
            </motion.div>
          )}

          {/* Yearly Chart */}
          {chartData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8"
            >
              <h2 className="font-heading text-lg mb-6">Yearly Trends</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="year" stroke="#71717A" />
                  <YAxis stroke="#71717A" domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#18181B",
                      border: "1px solid #27272A",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Bar dataKey="TeamPower" fill="#F97316" />
                  <Bar dataKey="Capability" fill="#3B82F6" />
                  <Bar dataKey="Collaboration" fill="#A855F7" />
                  <Bar dataKey="Energy" fill="#F59E0B" />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Yearly Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-zinc-800">
              <h2 className="font-heading text-lg">Yearly Breakdown</h2>
            </div>

            {yearlyData.years.length === 0 ? (
              <div className="p-12 text-center">
                <TrendingUp className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">No data available</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="yearly-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Events</th>
                      <th>Responses</th>
                      <th>TeamPower</th>
                      <th>Capability</th>
                      <th>Collaboration</th>
                      <th>Energy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyData.years.map((year) => (
                      <tr key={year.year}>
                        <td className="font-bold">{year.year}</td>
                        <td>{year.event_count}</td>
                        <td>{year.response_count}</td>
                        <td className="text-orange-400 font-bold">{year.avg_teampower_index}</td>
                        <td className="text-blue-400">{year.avg_capability_score}</td>
                        <td className="text-purple-400">{year.avg_collaboration_score}</td>
                        <td className="text-orange-400">{year.avg_energy_score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
